import { useState } from 'react'
import type { CustomerConfirmCandidate, CustomerConfirmProps } from '@/lib/render'
import { Card } from '@/components/ui/card'
import { useBranding } from '@/contexts/BrandingContext'

interface Props {
  props: unknown
  /**
   * Sends the operator's choice into the chat as their next message. The text
   * carries the chosen customer id — chat history is text-only, so an id that
   * never reaches the transcript is an id the model will later invent.
   */
  onQuickReply?: (text: string) => void
  /**
   * Operator surfaces only. Fires with the customer the operator picked so the
   * composer's customer pin follows the choice — confirming somebody here and
   * then having to pin the same person again is the same decision made twice.
   * Not fired by "No", which names nobody.
   */
  onCustomerPick?: (customer: CustomerConfirmCandidate) => void
}

// The operator-facing reply reads like something they could have typed; the id
// rides along in a [mentions: …] marker — the same authoritative-id line the
// composer's @-mentions use, which every message renderer already strips for
// display. The operator reads a name; the model still gets the id turns later.
export function confirmReplyText(candidate: CustomerConfirmCandidate): string {
  return `Yes — ${candidate.name} (${candidate.phone}) is the right customer.\n\n[mentions: ${candidate.name} = ${candidate.id}]`
}

export function pickAlternativeText(candidate: CustomerConfirmCandidate): string {
  return `I mean ${candidate.name} (${candidate.phone}).\n\n[mentions: ${candidate.name} = ${candidate.id}]`
}

export const NONE_OF_THESE_TEXT =
  'No — none of those is the right customer. Let me clarify who I mean.'

export function CustomerConfirmRenderer({ props, onQuickReply, onCustomerPick }: Props) {
  const branding = useBranding()
  const data = props as CustomerConfirmProps
  const [answered, setAnswered] = useState<string | null>(null)

  if (!data?.customer?.id || !data.customer.name) {
    return (
      <div className="rounded-lg bg-destructive/10 p-4">
        <p className="text-[13px] text-destructive">Invalid customer confirmation data</p>
      </div>
    )
  }

  const alternatives = (data.alternatives ?? []).filter(a => a?.id && a.name)
  const disabled = answered !== null || !onQuickReply

  const respond = (label: string, text: string, picked?: CustomerConfirmCandidate) => {
    if (disabled) return
    setAnswered(label)
    if (picked) onCustomerPick?.(picked)
    onQuickReply?.(text)
  }

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-col gap-1 px-4 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Confirm customer
        </p>
        <p className="text-sm text-foreground">
          {data.question ?? `Did you mean ${data.customer.name}?`}
        </p>
      </div>

      <div className="flex flex-col gap-0.5 px-4 pb-1 pt-2">
        <p className="text-[15px] font-bold text-foreground">{data.customer.name}</p>
        <p className="text-[13px] text-muted-foreground">{data.customer.phone}</p>
        {data.customer.context ? (
          <p className="text-[13px] text-muted-foreground">{data.customer.context}</p>
        ) : null}
      </div>

      <div className="flex gap-2 px-4 py-3">
        <button
          type="button"
          disabled={disabled}
          className="rounded-full px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: branding.primaryColor }}
          onClick={() => respond('yes', confirmReplyText(data.customer), data.customer)}
        >
          {answered === 'yes' ? '✓ Yes' : 'Yes, that’s them'}
        </button>
        <button
          type="button"
          disabled={disabled}
          className="rounded-full border border-border px-4 py-1.5 text-[13px] font-semibold text-foreground disabled:opacity-50"
          onClick={() => respond('none', NONE_OF_THESE_TEXT)}
        >
          {answered === 'none' ? '✓ No' : 'No'}
        </button>
      </div>

      {alternatives.length > 0 ? (
        <div className="flex flex-col gap-1.5 border-t border-border px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Or pick who you meant
          </p>
          {alternatives.map(alt => (
            <button
              key={alt.id}
              type="button"
              disabled={disabled}
              className="rounded-lg border border-border px-3 py-2 text-left disabled:opacity-50"
              onClick={() => respond(alt.id, pickAlternativeText(alt), alt)}
            >
              <span className="block text-[14px] font-semibold text-foreground">
                {answered === alt.id ? '✓ ' : ''}{alt.name}
              </span>
              <span className="block text-[12px] text-muted-foreground">
                {alt.phone}
                {alt.context ? ` · ${alt.context}` : ''}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </Card>
  )
}
